/**
 * @file Resolves a mapping-review session's bound-document schema into the
 * target universes the review surfaces offer. For an `entityRecords` import
 * it walks the entity's `defaultUILayout` into record-creation field options
 * (today's behavior). For a whole `document` it resolves the bound
 * document's schema — a `formVersion` binding chains `forms/<id>` →
 * `formTypes/<formType>`, a `record` binding loads `entities/<id>` — and
 * walks that layout — through `layoutUniverses` — into three universes: header
 * `fields`, reference slots (`references`), and the table list (`tables`,
 * `{ tableId, label }` for the per-table retarget picker). Per-table column
 * options come from the sibling
 * `useTableTypeFields`. Owns its own document subscriptions per consumer; the
 * formVersion case keeps two chained subscriptions live behind one snapshot.
 */
import { useContext, useEffect, useMemo } from 'react';
import { proxy, useSnapshot } from 'valtio';
import type {
  Entity,
  Form,
  FormType,
  MappingTarget,
} from '#types';
import { createDocumentStore } from '@/store';
import { layoutUniverses } from '@/services/editorExtractors';
import type { LayoutUniverses } from '@/services/editorExtractors';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import { EntitiesContext } from '@/providers/artifacts/EntitiesContext';

export interface TargetUniverses extends LayoutUniverses {
  loading: boolean;
}

const EMPTY: LayoutUniverses = { fields: [], references: [], tables: [] };

/** Stable empty store snapshot for the subscriptions a given target doesn't use. */
const EMPTY_STORE_STATE = proxy<{ loading: boolean; data: unknown; error: unknown }>({
  loading: false,
  data: null,
  error: null,
});

export function useTargetFields(target: MappingTarget): TargetUniverses {
  const { fieldTypes } = useContext(FieldTypeContext);
  const { entities } = useContext(EntitiesContext);

  const entityPath =
    target.kind === 'entityRecords'
      ? `entities/${target.entityId}`
      : target.binding.kind === 'record'
        ? `entities/${target.binding.entityId}`
        : null;
  const formPath = target.kind === 'document' && target.binding.kind === 'formVersion'
    ? `forms/${target.binding.formId}`
    : null;

  const entityStore = useMemo(
    () => (entityPath ? createDocumentStore<Entity>(entityPath) : null),
    [entityPath],
  );
  const formStore = useMemo(
    () => (formPath ? createDocumentStore<Form>(formPath) : null),
    [formPath],
  );
  useEffect(() => {
    if (!entityStore) return;
    void entityStore.connect();
    return () => entityStore.disconnect();
  }, [entityStore]);
  useEffect(() => {
    if (!formStore) return;
    void formStore.connect();
    return () => formStore.disconnect();
  }, [formStore]);

  const entitySnap = useSnapshot(entityStore?.state ?? EMPTY_STORE_STATE);
  const formSnap = useSnapshot(formStore?.state ?? EMPTY_STORE_STATE);
  const formTypeId = (formSnap.data as Form | null)?.formType;
  const formTypePath = formTypeId ? `formTypes/${formTypeId}` : null;
  const formTypeStore = useMemo(
    () => (formTypePath ? createDocumentStore<FormType>(formTypePath) : null),
    [formTypePath],
  );
  useEffect(() => {
    if (!formTypeStore) return;
    void formTypeStore.connect();
    return () => formTypeStore.disconnect();
  }, [formTypeStore]);
  const formTypeSnap = useSnapshot(formTypeStore?.state ?? EMPTY_STORE_STATE);

  return useMemo(() => {
    if (target.kind === 'entityRecords') {
      const data = entitySnap.data;
      if (!data) return { loading: entitySnap.loading, ...EMPTY };
      return { loading: false, ...layoutUniverses((data as Entity).defaultUILayout?.structure, fieldTypes, entities) };
    }
    if (target.binding.kind === 'record') {
      const data = entitySnap.data;
      if (!data) return { loading: entitySnap.loading, ...EMPTY };
      return { loading: false, ...layoutUniverses((data as Entity).defaultUILayout?.structure, fieldTypes, entities) };
    }
    const formType = formTypeSnap.data;
    if (!formType) return { loading: formSnap.loading || formTypeSnap.loading, ...EMPTY };
    return { loading: false, ...layoutUniverses((formType as FormType).defaultUILayout?.structure, fieldTypes, entities) };
  }, [target, entitySnap.data, entitySnap.loading, formSnap.loading, formTypeSnap.data, formTypeSnap.loading, fieldTypes, entities]);
}
